import React from 'react';
import { faCheckCircle, faExclamationCircle, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createPortal } from 'react-dom';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

type NotificationModalType = 'success' | 'error' | 'info';

interface TypeStyle {
  icon: IconDefinition;
  iconColor: string;
  iconBg: string;
  borderColor: string;
}

interface NotificationModalProps {
  title: string;
  message: string;
  type?: NotificationModalType;
  onClose: () => void;
  showCloseButton?: boolean;
}

function NotificationModal({ title, message, type = 'success', onClose, showCloseButton = true }: NotificationModalProps): React.ReactElement {
  const typeStyles: Record<NotificationModalType, TypeStyle> = {
    success: {
      icon: faCheckCircle,
      iconColor: 'text-green-600',
      iconBg: 'bg-green-100',
      borderColor: 'border-green-200'
    },
    error: {
      icon: faExclamationCircle,
      iconColor: 'text-red-600',
      iconBg: 'bg-red-100',
      borderColor: 'border-red-200'
    },
    info: {
      icon: faExclamationCircle,
      iconColor: 'text-blue-600',
      iconBg: 'bg-blue-100',
      borderColor: 'border-blue-200'
    }
  };

  const currentType = typeStyles[type] || typeStyles.success;

  const handleBackdropClick = (e: React.MouseEvent): void => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        className={`bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all border-2 ${currentType.borderColor}`}
      >
        <div className="p-6">
          {showCloseButton && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FontAwesomeIcon icon={faXmark} className="w-5 h-5" />
            </button>
          )}

          <div className="flex items-start space-x-4">
            <div className={`${currentType.iconBg} p-3 rounded-full flex-shrink-0`}>
              <FontAwesomeIcon
                icon={currentType.icon}
                className={`w-6 h-6 ${currentType.iconColor}`}
              />
            </div>
            <div className="flex-1 pt-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
              <p className="text-gray-600 leading-relaxed">{message}</p>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default NotificationModal;